import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import CalendarPage from "@/pages/Calendar";
import Clients from "@/pages/Clients";
import Services from "@/pages/Services";
import StaffPage from "@/pages/Staff";
import Reviews from "@/pages/Reviews";
import Settings from "@/pages/Settings";
import Inbox from "@/pages/Inbox";
import Sales from "@/pages/Sales";
import Account from "@/pages/Account";
import Admin from "@/pages/Admin";
import PublicBooking from "@/pages/PublicBooking";
import Layout from "@/components/Layout";

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="h-8 w-8 rounded-full border-2 border-stone-300 border-t-stone-800 animate-spin" />
    </div>
  );
}

function Protected({ children, roles, bare = false }) {
  const { user, business } = useAuth();
  if (user === null) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  if (user.role === "owner" && !user.business_id) return <Navigate to="/onboarding" replace />;
  return bare ? children : <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/book/:slug" element={<PublicBooking />} />
      <Route path="/book/:slug/payment/success" element={<PublicBooking />} />
      <Route path="/book/:slug/payment/cancel" element={<PublicBooking />} />
      <Route path="/onboarding" element={user === null ? <LoadingScreen /> : (user ? <Onboarding /> : <Navigate to="/login" replace />)} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/calendar" element={<Protected><CalendarPage /></Protected>} />
      <Route path="/clients" element={<Protected><Clients /></Protected>} />
      <Route path="/services" element={<Protected roles={["owner"]}><Services /></Protected>} />
      <Route path="/staff" element={<Protected roles={["owner"]}><StaffPage /></Protected>} />
      <Route path="/reviews" element={<Protected roles={["owner"]}><Reviews /></Protected>} />
      <Route path="/requests" element={<Protected roles={["owner"]}><Inbox /></Protected>} />
      <Route path="/sales" element={<Protected roles={["owner"]}><Sales /></Protected>} />
      <Route path="/settings" element={<Protected roles={["owner"]}><Settings /></Protected>} />
      <Route path="/admin" element={<Protected roles={["admin"]} bare><Admin /></Protected>} />
      <Route path="/account" element={<Account />} />
      <Route path="/:slug" element={<PublicBooking />} />
      <Route path="/:slug/payment/success" element={<PublicBooking />} />
      <Route path="/:slug/payment/cancel" element={<PublicBooking />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
